import {
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
  MutationKey,
  UseInfiniteQueryResult,
} from '@tanstack/react-query'

import { useGQLFetcher } from '../context/GQLClientProvider'
import { TypedDocumentNode } from '@graphql-typed-document-node/core'
import { Variables } from 'graphql-request'
import {
  EntityListResponse,
  EntityResponse,
  isEntityListResponse,
  isEntityResponse,
  processEntityListResponse,
  processEntityResponse,
} from '../lib/RequestUtils'
import { toastError } from '../lib/toastHelpers'

// This module includes utilty hooks abstracting all server-specific quirks of the Depoto BE
// in order to make our lifes easier, give us more out of work time and bring joy to our serene existence...

/*
    Usage example:
    ```
      const { data, error, isFetching, fetchNextPage, isFetchingNextPage } = useGQLInfinite(OrdersQuery, ['orders'])
    ```
    Where OrdersQuery is TypedDocumentNode – `const OrdersQuery = graphql('...')`) with $page gql variable and BasicPagination fragment
    passed to pagintaion key.
  */
export const useGQLInfinite = <TItem, TVariables extends Variables = Variables>(
  query: TypedDocumentNode<EntityListResponse<TItem>, TVariables>,
  queryKey: QueryKey,
  variables?: TVariables,
  options?: UseInfiniteQueryOptions<EntityListResponse<TItem>>,
): Omit<UseInfiniteQueryResult<TItem>, 'data'> & { data: TItem[] } => {
  const queryFn = useGQLFetcher(query)

  const { data, ...rest } = useInfiniteQuery({
    queryKey: [...queryKey, variables],
    queryFn: ({ pageParam }) => {
      const mergedVariables = variables ? { ...variables, ...{ page: pageParam } } : { page: pageParam }
      return queryFn(mergedVariables as unknown as TVariables)
    },
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      if (!isEntityListResponse(lastPage) || !lastPage) return null

      // Access paginator from the nested structure
      const dataKey = Object.keys(lastPage)[0]
      const pageData = lastPage[dataKey]

      if (typeof pageData?.paginator?.current !== 'number' || typeof pageData?.paginator?.endPage !== 'number') {
        return null
      }

      return pageData.paginator.endPage !== pageData.paginator.current ? pageData.paginator.current + 1 : null
    },
    ...options,
  })

  if (!data || !data.pages || !Array.isArray(data.pages)) {
    return {
      ...rest,
      data: [] as TItem[],
    } as Omit<UseInfiniteQueryResult<TItem>, 'data'> & { data: TItem[] }
  }

  return {
    data: data?.pages?.flatMap(page => {
      // Only process pages that are EntityListResponse
      if (isEntityListResponse(page)) {
        return processEntityListResponse(page)
      }
      return []
    }),
    ...rest,
  } as Omit<UseInfiniteQueryResult<TItem>, 'data'> & { data: TItem[] }
}

/*
    Usage example:
    ```    const { data, error, isFetching } = useGQLQuery(OrderQuery, ['order'], {
        id: 777,
      })
    ```
    Where OrderQuery is TypedDocumentNode – `const OrdersQuery = graphql('...')` with $id or any other variable that can be passed
    to third argument of useGQLQuery
  */
export const useGQLQuery = <TError = Error, TData = unknown, TVariables extends Variables = Variables>(
  query: TypedDocumentNode<EntityResponse<TData>, TVariables>,
  queryKey: QueryKey,
  variables?: TVariables,
  options?: Omit<UseQueryOptions<EntityResponse<TData>, TError, TData, QueryKey>, 'queryFn' | 'queryKey' | 'select'>,
) => {
  const queryFn = useGQLFetcher(query)

  const { data, ...rest } = useQuery({
    queryKey: [...queryKey, variables],
    queryFn: async () => await queryFn(variables),
    select: data => {
      if (isEntityResponse(data)) {
        return processEntityResponse(data)
      }
      throw new Error('Invalid response format')
    },
    ...options,
  })

  return {
    data,
    ...rest,
  }
}

/*
    Usage example:
    ```    const { mutate, isPending } = useGQLMutation(UpdateOrder, ['updateOrder', { id: 777 }]
    ```  Where UpdateOrder is TypedDocumentNode – `const UpdateOrder = graphql('...')` with any set of variables that can be passed
    as an argument to the mutate function in order to perform mutation.

    Please invalidate add the queries that needed to be invalidaded after mutation in `onSuccess` functions
    using queryClient or update query caches with response from mutation or procced with optimistic mutations
    (most likely they are inapplicable in our apps, but if you have a taste to over-engineering things, why not)
  */
export const useGQLMutation = <TError = Error, TData = unknown, TVariables extends Variables = Variables>(
  mutation: TypedDocumentNode<EntityResponse<TData>, TVariables>,
  mutationKey: MutationKey,
  options?: Omit<UseMutationOptions<EntityResponse<TData>, TError, TVariables>, 'mutationFn' | 'onSuccess'> & {
    onSuccess?: (data: TData, variables: TVariables, context: any) => void
    // Prepended to the error toast, e.g. 'Chyba dopravce' -> 'Chyba dopravce: <message>'
    errorPrefix?: string
    // Disable when the caller handles errors from mutateAsync itself.
    showErrorToast?: boolean
  },
) => {
  const mutationFn = useGQLFetcher(mutation)

  const { errorPrefix, showErrorToast = true, ...mutationOptions } = options ?? {}

  const { data, ...rest } = useMutation({
    ...mutationOptions,
    mutationKey: mutationKey,
    mutationFn: async variables => await mutationFn(variables),
    onError: error => {
      if (showErrorToast && error instanceof Error) {
        const message = error.message ?? 'Něco se pokazilo'
        toastError(errorPrefix ? `${errorPrefix}: ${message}` : message, {
          autoClose: false,
        })
      }
    },
    onSuccess: (data, variables, context) => {
      if (options?.onSuccess) {
        options.onSuccess(processEntityResponse(data), variables, context)
      }
    },
  })

  // useMutation does not have `select` option :sad_emoji:
  if (!data || !isEntityResponse(data)) {
    return {
      data: null,
      ...rest,
    }
  }

  return {
    data: processEntityResponse(data),
    ...rest,
  }
}
